import { createContext, type ReactNode, useContext } from 'react'
import { usePreferredGenresManager } from '@/hooks/genres/useUserPreferredGenres'
import type { Genre } from '@/types'

interface PreferredGenresContextValue {
  // Data
  preferredGenres: Genre[]
  suggestions: (Genre & { book_count: number })[]

  // State
  hasPreferredGenres: boolean
  isFirstTimeUser: boolean
  shouldShowSuggestions: boolean

  // Loading states
  isLoadingPreferred: boolean
  isLoadingSuggestions: boolean
  isLoading: boolean
  isUpdating: boolean

  // Actions
  updatePreferredGenres: (genreIds: string[]) => void
  updateError: Error | null

  // Utility functions
  isGenrePreferred: (genreId: string) => boolean
  getPreferredGenreIds: () => string[]
}

const PreferredGenresContext = createContext<PreferredGenresContextValue | null>(null)

interface PreferredGenresProviderProps {
  children: ReactNode
}

export function PreferredGenresProvider({ children }: PreferredGenresProviderProps) {
  const manager = usePreferredGenresManager()

  // Utility functions
  const isGenrePreferred = (genreId: string): boolean => {
    return manager.preferredGenres.some((genre) => genre.id === genreId)
  }

  const getPreferredGenreIds = (): string[] => {
    return manager.preferredGenres.map((genre) => genre.id)
  }

  const value: PreferredGenresContextValue = {
    // Data
    preferredGenres: manager.preferredGenres,
    suggestions: manager.suggestions,

    // State
    hasPreferredGenres: manager.hasPreferredGenres,
    isFirstTimeUser: manager.isFirstTimeUser,
    shouldShowSuggestions: manager.shouldShowSuggestions,

    // Loading states
    isLoadingPreferred: manager.isLoadingPreferred,
    isLoadingSuggestions: manager.isLoadingSuggestions,
    isLoading: manager.isLoading,
    isUpdating: manager.isUpdating,

    // Actions
    updatePreferredGenres: manager.updatePreferredGenres,
    updateError: manager.updateError,

    // Utility functions
    isGenrePreferred,
    getPreferredGenreIds,
  }

  return <PreferredGenresContext.Provider value={value}>{children}</PreferredGenresContext.Provider>
}

export function usePreferredGenresContext(): PreferredGenresContextValue {
  const context = useContext(PreferredGenresContext)

  if (!context) {
    throw new Error('usePreferredGenresContext must be used within a PreferredGenresProvider')
  }

  return context
}
